import { ExecuteResult, ExecutionContext, ListContainer, TextContainer, FormatterOptions, FormatterKind } from '../types';


export type FormatterContext = ExecutionContext


/**
 * 格式化器渲染器
 * @param value 值
 * @param ctx 上下文
 * @param options 选项 listType: 列表类型“ordered” | "unordered"
 * @returns 格式化后的文本
 */
export type FormatterRenderer<T> = (value: T, ctx: FormatterContext, options?: FormatterOptions) => string | undefined;

export interface FormatterEntry<T> {
  id: string;
  predicate: (value: unknown) => value is T;
  renderers: Partial<Record<FormatterKind, FormatterRenderer<T>>>;
  defaultFormat?: FormatterKind;
}

const registry: FormatterEntry<any>[] = [];

export const getRegisteredFormatters = (): FormatterEntry<any>[] => registry;

export const registerFormatter = <T>(entry: FormatterEntry<T>): void => {
  const existingIndex = registry.findIndex(item => item.id === entry.id);
  const normalizedEntry: FormatterEntry<T> = {
    ...entry,
    defaultFormat: entry.defaultFormat ?? 'xml',
  };

  if (existingIndex >= 0) {
    const existing = registry[existingIndex];
    registry[existingIndex] = {
      id: existing.id,
      predicate: existing.predicate || normalizedEntry.predicate,
      renderers: {
        ...existing.renderers,
        ...normalizedEntry.renderers,
      },
      defaultFormat: normalizedEntry.defaultFormat ?? existing.defaultFormat,
    };
    return;
  }

  registry.push(normalizedEntry);
};

export const renderExecuteResult = (
  context: FormatterContext,
  values: ExecuteResult[],
  kind?: FormatterKind,
  options?: FormatterOptions,
): string[] => {
  // 处理listContainer,展开所有元素
  const result = values.map(value => {
    if (isContainerWithFormatter(value) && value.type === 'listContainer') {
      let kindKey = kind && kind !== 'inherited' ? kind : value.defaultFormat;
      if (value.formatters?.[kindKey]) {
        return value.formatters?.[value.defaultFormat]?.(value, context, options);
      } else if (value.elements) {
        return value.elements.map(item => applyFormatter(context, item, kindKey, options));
      }
    }
    return applyFormatter(context, value, kind, options);
  }).flat().filter(d => d !== undefined);
  return result;
};

export const matchesType = (value: unknown, type: string): boolean =>
  typeof value === 'object' &&
  value !== null &&
  (value as Record<string, unknown>)['type'] === type;

const isContainerWithFormatter = (value: unknown): value is TextContainer | ListContainer<any> =>
  matchesType(value, 'textContainer') || matchesType(value, 'listContainer');


function applyFormatter(
  context: FormatterContext,
  value: ExecuteResult,
  kind?: FormatterKind,
  options?: FormatterOptions,
): string | undefined {
  const isContainer = isContainerWithFormatter(value);

  if (isContainer) {
    const formatterKey = kind && kind !== 'inherited' ? kind : value.defaultFormat;
    if (value.type === 'textContainer' && value.formatters?.[formatterKey]) {
      if (value.formatters?.[formatterKey]) {
        return value.formatters?.[value.defaultFormat]?.(value, context, options);
      } else if (value.reference) {
        return applyFormatter(context, value.reference, kind, options);
      }
    }
    return;
  }

  if (typeof value === 'object') {
    const formatter = registry.find(item => item.predicate(value));
    const formatterKey = kind && kind !== 'inherited' ? kind : formatter?.defaultFormat;
    if (formatter && formatterKey && formatter.renderers?.[formatterKey]) {
      return formatter.renderers[formatterKey](value, context, options);
    }
  }

  if (value !== undefined) {
    return String(value);
  }
}